import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  flattenInfiniteFilterData,
  useGetDropdownAPI,
} from '@legacy/apis/dropdownAPI';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
} from '@legacy/components/mui-components';
import { ResponseHandler } from '@legacy/components/utils/ResponseHandler';
import {
  useFilterDispatch,
  useFilterStore,
  useGetFilterValuesById,
  useGetParentQueryString,
  useMatchSelectedFilterKey,
} from '@legacy/stores/FilterStore';
import { useGetCurrentPageIdentifier } from '@legacy/stores/PageStore/pageStore.zustand';
import { stringToPascal } from '@legacy/utils/string';
import { translationAnyText } from '@legacy/utils/translation';
import { DataAutomationId } from '@legacy/utils/ts-utils';
import { useDebounce } from 'use-debounce';
import { FilterFeedback } from './components/FilterFeedback';
import { FilterInput } from './components/FilterInput';
import { useOnChangeHandler, useOnChangeSingleHandler } from './helpers';
import { IChildInputComponent, IOnChangeInput } from './types';

interface IChildInputComponentExtended
  extends IChildInputComponent,
    DataAutomationId {}

interface OptionType {
  label: string;
  value: string;
}

export const InputContainer = ({
  childFilters = [],
  contentUrl,
  fieldLabel,
  fieldPlaceholder,
  filterId,
  parentFilters = [],
  type,
  ...restProps
}: IChildInputComponentExtended) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue] = useDebounce(inputValue, 250);
  const dispatch = useFilterDispatch();
  const { selectedFilterList } = useFilterStore();
  const { t } = useTranslation('filter');

  const [isInclude, setIsInclude] = useState<string>(
    selectedFilterList[fieldLabel]?.isInclude === false ? 'is-not' : 'is',
  );

  const pageIdentifier = useGetCurrentPageIdentifier();
  const defaultValues = useGetFilterValuesById(fieldLabel);
  const matchChild = useMatchSelectedFilterKey(childFilters);
  const queryString = useGetParentQueryString(parentFilters);
  const itemsToFetch = 50;

  const { data, fetchNextPage, isError, isFetching, isLoading } =
    useGetDropdownAPI({
      contentUrl,
      filterId,
      pageIdentifier,
      queryString,
      searchText: debouncedInputValue,
      take: itemsToFetch,
    });
  const { dropdownList, latestPageChildrenLength } =
    flattenInfiniteFilterData(data);

  const onRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsInclude(e.target.value);

    if (dispatch && selectedFilterList[fieldLabel]) {
      dispatch({
        type: 'ADD_OR_UPDATE_FILTER',
        payload: {
          [fieldLabel]: {
            childFilters,
            contentUrl,
            isInclude: isInclude === 'is-not',
            label: fieldLabel,
            parentFilters,
            type,
            values: selectedFilterList[fieldLabel].values.map((filter) => ({
              label: filter.label,
              value: filter.value,
            })),
          },
        },
      });
      dispatch({
        type: 'QUERY_FILTER',
      });

      matchChild?.forEach((filterID) => {
        dispatch({ type: 'RESET_FILTER_ID', payload: { filterID } });
      });
    }
  };

  const [listValue, setListValue] = useState<Array<IOnChangeInput> | null>(
    (defaultValues as Array<IOnChangeInput>) || [],
  );

  const onChangeHandler = useOnChangeHandler({
    childFilters,
    contentUrl,
    fieldLabel,
    isInclude: isInclude === 'is',
    parentFilters,
    setListValue,
    type,
  });

  const onSingleChangeHandler = useOnChangeSingleHandler({
    childFilters,
    contentUrl,
    fieldLabel,
    isInclude: isInclude === 'is',
    parentFilters,
    setListValue,
    type,
  });

  return (
    <ResponseHandler isError={isError}>
      <Paper elevation={4} sx={{ padding: 1 }}>
        <Stack gap={2}>
          <Stack>
            {(type === 'MultiSelectTypeSearch' ||
              type === 'SingleSelectTypeSearch') && (
              <>
                {type === 'MultiSelectTypeSearch' && (
                  <FormControl>
                    <RadioGroup
                      name={`IsOrIsNot${filterId}Radio`}
                      onChange={onRadioChange}
                      row
                      sx={{ gap: 2, paddingInline: 2 }}
                      value={isInclude}
                    >
                      <FormControlLabel
                        control={<Radio />}
                        label={t('DropDownRadioIsLabel')}
                        value="is"
                      />
                      <FormControlLabel
                        control={<Radio />}
                        label={t('DropDownRadioIsNotLabel')}
                        value="is-not"
                      />
                    </RadioGroup>
                  </FormControl>
                )}
                <FilterInput
                  autoHighlight
                  data-automation-id={restProps['data-automation-id']}
                  fetchNextPage={fetchNextPage}
                  fieldLabel={fieldLabel}
                  filterId={filterId}
                  filterSelectedOptions
                  inputValue={inputValue}
                  isOptionEqualToValue={(
                    option: OptionType,
                    value: OptionType,
                  ) => option.value === value.value}
                  label={translationAnyText(
                    t,
                    `FilterInputName${stringToPascal(fieldLabel)}`,
                  )}
                  loading={isFetching || isLoading}
                  onChange={onChangeHandler}
                  onInputChange={(
                    _: ChangeEvent<HTMLInputElement>,
                    newInputValue: string,
                  ) => setInputValue(newInputValue)}
                  openOnFocus={matchChild.length <= 0}
                  options={dropdownList}
                  latestPageChildrenLength={latestPageChildrenLength}
                  take={itemsToFetch}
                  placeholder={fieldPlaceholder}
                  type={type}
                  value={
                    type === 'MultiSelectTypeSearch'
                      ? listValue
                      : (listValue?.[0] ?? null)
                  }
                />
              </>
            )}
            {type === 'Checkbox' && (
              <FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={listValue?.[0]?.value === 'true'}
                      data-automation-id={restProps['data-automation-id']}
                      name={`Hide${filterId}CheckboxNo`}
                      onChange={onSingleChangeHandler}
                    />
                  }
                  label={t('FilterHideText')}
                />
              </FormControl>
            )}
          </Stack>

          <FilterFeedback matchChildren={matchChild} />
        </Stack>
      </Paper>
    </ResponseHandler>
  );
};
